import {
  Button,
  EButtonColorVariant,
  ETextStyleVariant,
  GeneralManageVehiclesFillIcon,
  GeneralManageVehiclesOutlineIcon,
  GeneralMapPinFillIcon,
  GeneralMapPinOutlineIcon,
  GeneralSunSunFilledIcon,
  GeneralSunSunIcon,
  Text,
} from '@outdoorsyco/bonfire';
import cn from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';

import Link from '@/components/switchback/Link/Link';
import { RV_CAMPSITE_BUNDLES_LANDING_PAGE, RV_DELIVERY_HOMEPAGE } from '@/constants/urls';
import { trackEscapesPillOnHome } from '@/services/analytics/escapes';
import { EEscapesSource, EEscapesTab } from '@/services/analytics/escapes/types';
import { useExperimentIsEnabled } from '@/services/experiments';
import { OptimizelyFlags } from '@/services/experiments/flags';

export enum ETabsVariant {
  Rentals,
  Delivery,
  Escapes,
}

export enum ETabsColorVariant {
  White,
  Black,
}

interface ITabsProps {
  tabVariant: ETabsVariant;
  colorVariant?: ETabsColorVariant;
}

const getEventType = (tab: ETabsVariant): EEscapesTab | null => {
  switch (tab) {
    case ETabsVariant.Rentals:
      return EEscapesTab.RV_RENTALS;
    case ETabsVariant.Escapes:
      return EEscapesTab.ESCAPES;
    case ETabsVariant.Delivery:
      return EEscapesTab.DELIVERY;
    default:
      return null;
  }
};

export const Tabs: React.FC<ITabsProps> = ({
  tabVariant,
  colorVariant = ETabsColorVariant.Black,
}) => {
  const intl = useIntl();
  const isRentalsVDeliverySearchModeEnabled = useExperimentIsEnabled(
    OptimizelyFlags.RENTAL_V_DELIVERY_SEARCH_MODE,
  );

  const tabs = [
    {
      label: isRentalsVDeliverySearchModeEnabled
        ? intl.formatMessage({ defaultMessage: 'Drivable RVs', id: 'BgHmQr' })
        : intl.formatMessage({ defaultMessage: 'RV Rentals', id: 'ctb+gI' }),
      variant: ETabsVariant.Rentals,
      ActiveIcon: GeneralManageVehiclesFillIcon,
      InactiveIcon: GeneralManageVehiclesOutlineIcon,
      url: '/',
    },
    ...(isRentalsVDeliverySearchModeEnabled
      ? [
          {
            label: intl.formatMessage({ defaultMessage: 'Stationary RVs', id: 'cJN+Zj' }),
            variant: ETabsVariant.Delivery,
            ActiveIcon: GeneralMapPinFillIcon,
            InactiveIcon: GeneralMapPinOutlineIcon,
            url: RV_DELIVERY_HOMEPAGE,
          },
        ]
      : []),
    {
      label: intl.formatMessage({ defaultMessage: 'Escapes', id: 'HIf3wo' }),
      variant: ETabsVariant.Escapes,
      ActiveIcon: GeneralSunSunFilledIcon,
      InactiveIcon: GeneralSunSunIcon,
      url: RV_CAMPSITE_BUNDLES_LANDING_PAGE,
    },
  ];

  const onClick = (tab: ETabsVariant) => {
    const eventType = getEventType(tab);
    const label = tabs.find(t => t.variant === tab)?.label;
    if (!label) return;
    trackEscapesPillOnHome({
      tab: eventType || label,
      source: EEscapesSource.PRODUCT_OFFERINGS,
    });
  };

  return (
    <div className="flex py-1 overflow-x-auto scrollbar-none gap-1 md:gap-3">
      {tabs.map(({ label, url, variant, ActiveIcon, InactiveIcon }, index) => {
        const isActive = variant === tabVariant;
        const IconComponent = isActive ? ActiveIcon : InactiveIcon;
        return (
          <Link
            href={url}
            key={index}
            passHref
            forceClientsideRouting
            legacy
            onClick={() => onClick(variant)}>
            <Button
              label={
                <div className="flex items-center gap-2">
                  <IconComponent className="w-5 h-5 text-700" />
                  <Text variant={ETextStyleVariant.MediumBold} className="text-300 md:text-500">
                    {label}
                  </Text>
                </div>
              }
              variant={isActive ? EButtonColorVariant.Tertiary : EButtonColorVariant.Secondary}
              className={cn('border whitespace-nowrap', {
                '!bg-gray-800': isActive,
                '!bg-canvas-150': colorVariant === ETabsColorVariant.White && !isActive,
                'py-1 px-3 md:py-2.5 md:px-5': isRentalsVDeliverySearchModeEnabled,
                'py-2 px-4': !isRentalsVDeliverySearchModeEnabled,
              })}
              aria-label={intl.formatMessage(
                { defaultMessage: 'Navigate to {tabName}', id: 'w4XjBW' },
                { tabName: label },
              )}
              onClick={() => onClick(variant)}
            />
          </Link>
        );
      })}
    </div>
  );
};
